/**
 * PlayerMooringStations.ts — encapsulates the player boat's mooring
 * cleats: each station owns a {@link MooringLine} (force model), a
 * fairlead offset (visual rope start), and a slot in the shared
 * {@link MooringRopeRenderer}.
 *
 * Three responsibilities live here, all tied to the same per-cleat
 * data (body coords, fairlead, rope slot, line):
 *  1. **Configuration** — `addStation(...)` resolves the cleat's body
 *     coordinates from required GLB `Cleat.*` markers and allocates the
 *     visual rope slice.
 *  2. **Physics** — `applyMooringPhysics(dt, state)` reads the per-
 *     cleat target id from {@link AppState.mooringTargets}, looks up
 *     the live anchor, sets `line.active`/`anchor`/`restLength`, and
 *     ticks the elastic line so the boat actually feels the tension.
 *  3. **Rendering** — `updateRopeGeometry(state)` refreshes the rope
 *     mesh between the cleat fairlead and the anchor each frame.
 *
 * The orchestrator (`main.ts`) only wires up the dependencies once and
 * then calls `applyMooringPhysics` (before integrating the boat) and
 * `updateRopeGeometry` (after integration). Pickup queries are routed
 * through {@link MooringPickupService}, registered automatically per
 * station.
 */
import type { Boat } from "./Boat";
import type { MooringAnchor } from "./Dock";
import { MooringLine } from "./MooringLine";
import type { MooringRopeRenderer } from "../rendering/MooringRopeRenderer";
import type { MooringPickupService } from "./MooringPickupService";
import type { AppState } from "../State";
import { PLAYER_ANCHOR_ID } from "./DeployedAnchor";
import { getBoatCleatPosition, type BoatCleatKey } from "./BoatCleats";
import {
  projectBodyPointOutsideBoatCollisionHull,
  projectBodySegmentOutsideBoatCollisionHull,
} from "./Collisions";

/** Tunables for the elastic line model — same values for every cleat. */
const LINE_K_SPRING = 1200;
const LINE_K_DAMPER = 180;
const LINE_DEFAULT_REST_LENGTH = 8;

// --- Visual rope solver (Verlet + distance constraints + hull SDF) ---
// Classic Jakobsen rope (Hitman 2001): we keep two snapshots of every rope
// point in BODY frame, integrate by Verlet so the rope has momentum and
// swings when the boat turns, then relax distance + hull-collision
// constraints. Endpoints are pinned to fairlead and anchor each frame.
const VISUAL_ROPE_POINTS = 28;
const VISUAL_ROPE_ITERATIONS = 8;
const VISUAL_ROPE_MAX_SLACK = 5;
const VISUAL_ROPE_DAMPING = 0.9;
const VISUAL_ROPE_MAX_DT = 1 / 30;
const HULL_COLLISION_MARGIN = 0.08;
const FAIRLEAD_CONTACT_FREE_LENGTH = 0.8;
// Effective downward acceleration (body frame, m/s²) per kg of rode mass
// per metre. Tuned visually — not a real density; controls catenary depth.
const VISUAL_GRAVITY_BASE = 0.4;
const VISUAL_GRAVITY_PER_KG = 1.4;
const VISUAL_GRAVITY_MAX = 5.0;
const CUMA_ROPE_WEIGHT_KG_PER_M = 0.08;

interface Station {
  cleatName: BoatCleatKey;
  cleatBody: readonly [number, number, number];
  fairleadBody: readonly [number, number, number];
  line: MooringLine;
  /** Slot index in the rope-styled renderer. */
  ropeHandle: number;
  /** Slot index in the chain-styled renderer. */
  chainHandle: number;
  /** Verlet rope state in body frame: current positions. */
  visualPos?: [number, number, number][];
  /** Verlet rope state in body frame: previous positions. */
  visualPrev?: [number, number, number][];
}

export interface PlayerMooringStationsDeps {
  playerBoat: Boat;
  /** Renderer for rope-styled lines (hemp colour, thin). */
  ropeRenderer: MooringRopeRenderer;
  /** Renderer for chain-styled lines (dark steel, thicker). */
  chainRenderer: MooringRopeRenderer;
  pickupService: MooringPickupService;
  /** Shared map of all known mooring anchors (bollards + buoys). */
  allAnchors: ReadonlyMap<string, MooringAnchor>;
  /**
   * Initial target for newly-allocated `MooringLine` objects. Anchors
   * are overridden every frame from `AppState.mooringTargets`, so the
   * choice is irrelevant — the only constraint is that it must be a
   * valid `MooringAnchor` so the line constructor doesn't crash.
   */
  initialAnchor: MooringAnchor;
}

export class PlayerMooringStations {
  private readonly stations: Station[] = [];

  constructor(private readonly deps: PlayerMooringStationsDeps) {}

  /**
   * Add one mooring station for the named cleat. Body coordinates are taken
   * from required GLB `Cleat.*` markers. The fairlead (where the rope visually
   * exits the deck) sits a short offset away from the cleat.
   */
  addStation(
    cleatName: BoatCleatKey,
    fairleadDX: number,
    fairleadDY: number,
    fairleadOverride?: readonly [number, number, number],
  ): void {
    const cleatBody = this.cleatBodyFor(cleatName);
    const fairleadBody: readonly [number, number, number] =
      fairleadOverride ?? [
        cleatBody[0] + fairleadDX,
        cleatBody[1] + fairleadDY,
        cleatBody[2],
      ];
    const line = new MooringLine({
      boat: this.deps.playerBoat,
      boatCleatBody: [cleatBody[0], cleatBody[1], cleatBody[2]],
      anchor: this.deps.initialAnchor,
      restLength: LINE_DEFAULT_REST_LENGTH,
      kSpring: LINE_K_SPRING,
      kDamper: LINE_K_DAMPER,
      active: false,
    });
    this.stations.push({
      cleatName,
      cleatBody,
      fairleadBody,
      line,
      ropeHandle: this.deps.ropeRenderer.addLine(),
      chainHandle: this.deps.chainRenderer.addLine(),
    });
    this.deps.pickupService.registerStation({ cleatName, fairleadBody });
  }

  /**
   * Current world-space distance from a cleat's fairlead to its
   * currently-bound anchor, in metres. Returns null if the cleat
   * has no station registered or no active anchor (i.e. the line
   * is loose / stowed). Used by the "Wybierz / Luzuj" UI to
   * compute how much to shorten or lengthen the rope.
   */
  getFairleadAnchorDistance(cleatName: string): number | null {
    const st = this.stations.find((s) => s.cleatName === cleatName);
    if (!st || !st.line.active) return null;
    const boat = this.deps.playerBoat;
    const [fbX, fbY, fbZ] = st.fairleadBody;
    const [ax, az] = boat.bodyToWorld(fbX, fbY, fbZ);
    const ay = boat.posY + fbY;
    const [bx, by, bz] = st.line.anchor.getWorldPos();
    const dx = ax - bx;
    const dy = ay - by;
    const dz = az - bz;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * Resolve cleat body coordinates from required GLB `Cleat.*` markers.
   * Missing authoring data fails loudly instead of producing misleading rope
   * points.
   */
  private cleatBodyFor(
    name: BoatCleatKey,
  ): readonly [number, number, number] {
    const pos = getBoatCleatPosition(this.deps.playerBoat.params, name, {
      requireMarker: true,
    });
    if (!pos) {
      throw new Error(`Missing player boat cleat marker ${name}`);
    }
    return [pos[0], pos[1], pos[2]];
  }

  /**
   * Per-frame physics tick: bind each line to its currently-assigned
   * anchor (read from {@link AppState.mooringTargets}) and integrate
   * one step. Must be called BEFORE the boat integrates so the
   * tension forces are consumed in the same frame.
   */
  applyMooringPhysics(dt: number, state: AppState): void {
    for (const st of this.stations) {
      const targetId = state.mooringTargets[st.cleatName] ?? null;
      const anchor = targetId ? this.deps.allAnchors.get(targetId) : undefined;
      if (anchor) {
        st.line.anchor = anchor;
        st.line.setBoatCleatBody(
          targetId === PLAYER_ANCHOR_ID ? st.fairleadBody : st.cleatBody,
        );
        st.line.restLength =
          state.mooringRestLengths[st.cleatName] ?? st.line.restLength;
        st.line.active = true;
      } else {
        st.line.active = false;
      }
      st.line.step(dt);
    }
  }

  /**
   * Per-frame visual update: refresh the rope geometry between cleat
   * fairlead and anchor for every active station; hide inactive
   * slots. Call AFTER the boat has integrated so the fairlead world
   * position is up to date.
   */
  updateRopeGeometry(dt: number, state: AppState): void {
    const boat = this.deps.playerBoat;
    const ropeRenderer = this.deps.ropeRenderer;
    const chainRenderer = this.deps.chainRenderer;
    const clampedDt = Math.min(Math.max(dt, 1e-4), VISUAL_ROPE_MAX_DT);
    const rodeIsChain =
      boat.params.anchorRodeType === "chain" ||
      boat.params.anchorRodeType === "mixed";
    const anchorWeight = boat.params.anchorRodeWeightKgPerM;
    for (const st of this.stations) {
      if (!st.line.active) {
        ropeRenderer.hideLine(st.ropeHandle);
        chainRenderer.hideLine(st.chainHandle);
        st.visualPos = undefined;
        st.visualPrev = undefined;
        continue;
      }
      const [fbX, fbY, fbZ] = st.fairleadBody;
      const [bx, by, bz] = st.line.anchor.getWorldPos();
      const isAnchorRode = st.line.anchor.anchorId === PLAYER_ANCHOR_ID;
      // Anchor rode adopts the boat's rode type; cumy are always rope.
      const useChain = isAnchorRode && rodeIsChain;
      const weightKgPerM = isAnchorRode
        ? anchorWeight
        : CUMA_ROPE_WEIGHT_KG_PER_M;
      const gravity = Math.min(
        VISUAL_GRAVITY_MAX,
        VISUAL_GRAVITY_BASE + weightKgPerM * VISUAL_GRAVITY_PER_KG,
      );
      const path = this.solveVisualRopePath(
        st,
        [fbX, fbY, fbZ],
        [bx, by, bz],
        state.waterLevel,
        clampedDt,
        gravity,
      );
      if (useChain) {
        ropeRenderer.hideLine(st.ropeHandle);
        chainRenderer.updatePolyline(st.chainHandle, path);
      } else {
        chainRenderer.hideLine(st.chainHandle);
        ropeRenderer.updatePolyline(st.ropeHandle, path);
      }
    }
  }

  private solveVisualRopePath(
    st: Station,
    fairleadBody: readonly [number, number, number],
    anchorWorld: readonly [number, number, number],
    _waterLevel: number,
    dt: number,
    gravity: number,
  ): [number, number, number][] {
    const boat = this.deps.playerBoat;
    const anchorBody = this.worldPointToBody(anchorWorld);
    const chord = this.distance3(fairleadBody, anchorBody);
    // Use the rope's natural rest length (clamped to a visual slack budget)
    // as the segment-length basis, NOT max(chord, …). Tying segLen to
    // chord means it grows whenever the boat moves away from the anchor —
    // and the one-sided cable constraint can't expand pre-compressed
    // segments, so slack collects as a "lump" near the anchor while the
    // rest of the rope stays straight. Keeping segLen independent of chord
    // means a stretched rope hits a hard maximum reach (constraint pulls
    // segments taut) and a slack rope just sags under gravity.
    const visibleLength = Math.min(
      st.line.restLength,
      chord + VISUAL_ROPE_MAX_SLACK,
    );
    const segmentLength = visibleLength / (VISUAL_ROPE_POINTS - 1);

    const needsReseed =
      !st.visualPos ||
      !st.visualPrev ||
      st.visualPos.length !== VISUAL_ROPE_POINTS ||
      this.distance3(st.visualPos[0]!, fairleadBody) > 1.5 ||
      this.distance3(st.visualPos[st.visualPos.length - 1]!, anchorBody) > 6;

    if (needsReseed) {
      st.visualPos = this.initialRopeBodyPoints(
        fairleadBody,
        anchorBody,
        visibleLength,
      );
      st.visualPrev = st.visualPos.map(
        (p) => [p[0], p[1], p[2]] as [number, number, number],
      );
    }

    const pos = st.visualPos!;
    const prev = st.visualPrev!;
    const N = pos.length;

    // --- Verlet integration for interior points ---
    // Endpoints (fairlead, anchor) are pinned every frame; interior points
    // carry momentum from the previous frame so the rope can swing.
    const gravDt2 = -gravity * dt * dt;
    for (let i = 1; i < N - 1; i++) {
      const p = pos[i]!;
      const q = prev[i]!;
      const vx = (p[0] - q[0]) * VISUAL_ROPE_DAMPING;
      const vy = (p[1] - q[1]) * VISUAL_ROPE_DAMPING;
      const vz = (p[2] - q[2]) * VISUAL_ROPE_DAMPING;
      q[0] = p[0];
      q[1] = p[1];
      q[2] = p[2];
      p[0] = p[0] + vx;
      p[1] = p[1] + vy + gravDt2;
      p[2] = p[2] + vz;
    }
    // Pin endpoints (also overwrite prev so they don't "swing").
    pos[0] = [fairleadBody[0], fairleadBody[1], fairleadBody[2]];
    pos[N - 1] = [anchorBody[0], anchorBody[1], anchorBody[2]];
    prev[0] = [fairleadBody[0], fairleadBody[1], fairleadBody[2]];
    prev[N - 1] = [anchorBody[0], anchorBody[1], anchorBody[2]];

    const preferredSide =
      Math.abs(anchorBody[2]) > 0.05 ? Math.sign(anchorBody[2]) : 1;

    // --- Constraint relaxation: distance + Hull-derived rope collision ---
    for (let iter = 0; iter < VISUAL_ROPE_ITERATIONS; iter++) {
      this.solveRodeDistances(pos, segmentLength);
      for (let i = 1; i < N - 1; i++) {
        const p = pos[i]!;
        if (this.distance3(p, fairleadBody) < FAIRLEAD_CONTACT_FREE_LENGTH) {
          continue;
        }
        const q = prev[i]!;
        const beforeX = p[0];
        const beforeZ = p[2];
        projectBodyPointOutsideBoatCollisionHull(boat, p, {
          preferredSide,
          sideLockThreshold: 0.18,
          margin: HULL_COLLISION_MARGIN,
        });
        q[0] += p[0] - beforeX;
        q[2] += p[2] - beforeZ;
      }
      for (let i = 0; i < N - 1; i++) {
        const a = pos[i]!;
        const b = pos[i + 1]!;
        const midDistToFairlead = this.distance3(
          [
            (a[0] + b[0]) * 0.5,
            (a[1] + b[1]) * 0.5,
            (a[2] + b[2]) * 0.5,
          ],
          fairleadBody,
        );
        if (midDistToFairlead < FAIRLEAD_CONTACT_FREE_LENGTH) continue;
        const ax = a[0];
        const az = a[2];
        const bx = b[0];
        const bz = b[2];
        projectBodySegmentOutsideBoatCollisionHull(boat, pos[i]!, pos[i + 1]!, {
          preferredSide,
          sideLockThreshold: 0.18,
          margin: HULL_COLLISION_MARGIN,
          moveA: i > 0,
          moveB: i + 1 < N - 1,
        });
        if (i > 0) {
          prev[i]![0] += a[0] - ax;
          prev[i]![2] += a[2] - az;
        }
        if (i + 1 < N - 1) {
          prev[i + 1]![0] += b[0] - bx;
          prev[i + 1]![2] += b[2] - bz;
        }
      }
    }

    return pos.map((p) => this.bodyPointToWorld(p));
  }

  private initialRopeBodyPoints(
    fairleadBody: readonly [number, number, number],
    anchorBody: readonly [number, number, number],
    visibleLength: number,
  ): [number, number, number][] {
    const points: [number, number, number][] = [];
    const chord = this.distance3(fairleadBody, anchorBody);
    const sag = Math.min(2.5, Math.max(0, visibleLength - chord) * 0.35);
    for (let i = 0; i < VISUAL_ROPE_POINTS; i++) {
      const t = i / (VISUAL_ROPE_POINTS - 1);
      const drop = 4 * sag * t * (1 - t);
      points.push([
        fairleadBody[0] + (anchorBody[0] - fairleadBody[0]) * t,
        fairleadBody[1] + (anchorBody[1] - fairleadBody[1]) * t - drop,
        fairleadBody[2] + (anchorBody[2] - fairleadBody[2]) * t,
      ]);
    }
    return points;
  }

  private solveRodeDistances(
    points: [number, number, number][],
    segmentLength: number,
  ): void {
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]!;
      const b = points[i + 1]!;
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const dz = b[2] - a[2];
      const d = Math.hypot(dx, dy, dz);
      if (d < 1e-5) continue;
      // One-sided cable constraint: only pull when stretched beyond the
      // segment length, never push when compressed. Two-sided distance
      // constraints + a slack rope have many zigzag equilibria — making
      // the constraint inequality lets gravity uniquely determine the
      // catenary shape.
      if (d <= segmentLength) continue;
      const correction = (d - segmentLength) / d;
      const aWeight = i === 0 ? 0 : 0.5;
      const bWeight = i === points.length - 2 ? 0 : 0.5;
      const totalWeight = aWeight + bWeight;
      if (totalWeight <= 0) continue;
      const ax = dx * correction * (aWeight / totalWeight);
      const ay = dy * correction * (aWeight / totalWeight);
      const az = dz * correction * (aWeight / totalWeight);
      const bx = dx * correction * (bWeight / totalWeight);
      const by = dy * correction * (bWeight / totalWeight);
      const bz = dz * correction * (bWeight / totalWeight);
      a[0] += ax;
      a[1] += ay;
      a[2] += az;
      b[0] -= bx;
      b[1] -= by;
      b[2] -= bz;
    }
  }

  private worldPointToBody(
    world: readonly [number, number, number],
  ): [number, number, number] {
    const boat = this.deps.playerBoat;
    const dx = world[0] - boat.posX;
    const dz = world[2] - boat.posZ;
    const ch = Math.cos(boat.heading);
    const sh = Math.sin(boat.heading);
    return [dx * sh + dz * -ch, world[1] - boat.posY, dx * ch + dz * sh];
  }

  private distance3(
    a: readonly [number, number, number],
    b: readonly [number, number, number],
  ): number {
    return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  }

  private bodyPointToWorld(
    body: readonly [number, number, number],
  ): [number, number, number] {
    const boat = this.deps.playerBoat;
    const [x, z] = boat.bodyToWorld(body[0], body[1], body[2]);
    return [x, boat.posY + body[1], z];
  }
}
